/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AclsDetailsComponent,
    AddEditCertComponent,
    AddEditCSRComponent,
    AddEditHealthChecksComponent,
    AddRestoreNodeComponent,
    AdvancedServicesComponent,
    CipherListComponent,
    HaConfComponent,
    HealthChecksComponent,
    L7StickTableComponent,
    ManageACLsComponent,
    ManageServicesComponent,
    PulseConfComponent,
    ServiceDetailsComponent,
    SSLCertificateComponent,
    UploadPemPfxComponent
} from './pages/clusterConfiguration';
import { AclsDetailsModule } from './pages/clusterConfiguration/manageACLs/aclDetails/acls-details.module';
import {
    AclsResolver,
    AddEditCipherListComponent,
    AddEditRegionComponent,
    BackendsResolver,
    ClusterStatusResolver,
    EnduranceRbacApiService,
    EnduranceRbacMenuResolvers,
    EnduranceRBACResourcesApiService,
    FrontendsResolver,
    HostnameResolver,
    InterfaceResolver,
    LicenceResolver,
    PulseHaServicesResolvers,
    RegionDetailsComponent,
    RegionsComponent,
    RoutingResolver,
    ServicesUtilsService,
    StickTableResolver,
    SupportApiService
} from './shared';
import {
    AddBondComponent,
    AddEditFipComponent,
    AddEditIpGroupsComponent,
    AddEditSipComponent,
    AddEditVlanComponent,
    BreakBondComponent,
    EditInterfaceComponent,
    FipsIpsComponent,
    InterfaceConfigurationComponent,
    IpGroupsComponent,
    SipsComponent
} from './pages/network';
import {
    AddEditFallbackPageComponent,
    BackupRestoreComponent,
    EnduranceAddEditGroupsComponent,
    EnduranceAddEditRolesComponent,
    EnduranceAddEditUserComponent,
    EnduranceUserListComponent,
    FallbackPagesComponent,
    ManualUpdateComponent,
    RestoreBackupComponent,
    ServicesComponent,
    SystemUpdateComponent,
    UploadBackupComponent
} from './pages/maintenance';
import {
    AddEditHealthChecksModule
} from './pages/clusterConfiguration/healthchecks/addEditHealthChecks/add-edit-healthchecks.module';
import { AdvancedServicesModule } from './pages/clusterConfiguration/advancedServices/advancedServices.module';
import { AuthGuard, EnvironmentGuard, RbacPageGuard, SocketGuard } from '../../core/guards';
import { BackupRestoreModule } from './pages/maintenance/backupRestore/backupRestore.module';
import { CipherListModule } from './pages/clusterConfiguration/cipherList/cipherList.module';
import { ConfigurationComponent } from './pages/viewConfiguration/viewConf/configurations.component';
import { ConfigurationsModule } from './pages/viewConfiguration/viewConf/configurations.module';
import { ContactUsComponent, TechnicalSupportDownloadComponent } from './pages/support';
import { ContactUsModule } from './pages/support/details/contact-us.module';
import {
    DateTimeComponent,
    ExecuteComponent,
    HostnameDnsComponent,
    LicenceComponent,
    PhysicalAdvConfComponent,
    PortalManagementComponent,
    PrometheusComponent,
    RoutingComponent,
    SnmpComponent,
    SnmpConfigComponent,
    SnmpUserComponent,
    UserInterfaceComponent,
} from './pages/localConfiguration';
import { DateTimeModule } from './pages/localConfiguration/dateTime/dateTime.module';
import { EnduranceAddEditRolesModule } from './pages/maintenance/users/addEditRoles/endurance-add-edit-roles.module';
import { EnduranceResolverGuard } from './shared/guards/endurance-resolver-guard.service';
import { EnduranceUserListModule } from './pages/maintenance/users/endurance-user-list.module';
import { ExecuteModule } from './pages/localConfiguration/execute/execute.module';
import { FallbackPagesModule } from './pages/maintenance/fallbackPages/fallbackPages.module';
import { FipsIpsModule } from './pages/network/floatingIps/ips/fips.ips.module';
import { GslbModule } from './pages/clusterConfiguration/gslb/gslb.module';
import { GslbReportsModule } from './pages/reports/gslb-reports/gslb-reports.module';
import { HaConfModule } from './pages/clusterConfiguration/haConfiguration/haConf.module';
import { HealthChecksModule } from './pages/clusterConfiguration/healthchecks/health-checks.module';
import { HostnameDnsModule } from './pages/localConfiguration/hostnameDns/hostnameDns.module';
import { IpGroupsModule } from './pages/network/groups/ip-groups.module';
import { L7StickTableModule } from './pages/clusterConfiguration/l7StickTable/l7StickTable.module';
import { LicenceModule } from './pages/localConfiguration/licence/licence.module';
import { LoadbalancerComponent } from './loadbalancer.component';
import { LoadbalancerSharedModule } from './shared/loadbalancer.shared.module';
import { LogsComponent } from './pages/viewLogs/logs/logs.component';
import { LogsModule } from './pages/viewLogs/logs/logs.module';
import { ManageACLsModule } from './pages/clusterConfiguration/manageACLs/manageACLs.module';
import { ManageServicesModule } from './pages/clusterConfiguration/manageServices/manageServices.module';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { NetworkModule } from './pages/network/network.module';
import { NewServiceComponent } from './pages/full-width-pages/addEditServiceWizard/newService.component';
import { NewServiceModule } from './pages/full-width-pages/addEditServiceWizard/newService.module';
import { NgOptimizedImage } from '@angular/common';
import { NoAccessComponent, PageErrorComponent } from '../../core/components';
import { NotificationsComponent } from './pages/notifications/notifications.component';
import { NotificationsModule } from './pages/notifications/notifications.module';
import { OverviewComponent } from './pages/systemOverview/overview.component';
import { OverviewDetailsComponent } from './pages/systemOverview/overview-details/overview-details.component';
import { OverviewModule } from './pages/systemOverview/overview.module';
import { PhysicalAdvConfigModule } from './pages/localConfiguration/physicalAdvConfiguration/physicalAdvConf.module';
import { PortalManagementModule } from './pages/localConfiguration/portal-management/portal_management.module';
import { PrometheusModule } from './pages/localConfiguration/collectors/prometheus/prometheus.module';
import { PulseConfModule } from './pages/clusterConfiguration/pulseConfiguration/pulseConf.module';
import { RouterModule } from '@angular/router';
import { RoutingModule } from './pages/localConfiguration/routing/routing.module';
import { SSLCertificateModule } from './pages/clusterConfiguration/sslCertificate/ssl-certificate.module';
import { ServiceDetailsModule } from './pages/clusterConfiguration/manageServices/serviceDetails/serviceDetails.module';
import { ServicesModule } from './pages/maintenance/services/services.module';
import { SharedModule } from '../../core/shared.module';
import { SipsModule } from './pages/network/staticIps/sips.module';
import { SnmpModule } from './pages/localConfiguration/snmpConfiguration/snmp.module';
import { StaticRoutesModule } from './pages/network/staticRoutes/static-routes.module';
import { SystemUpdateModule } from './pages/maintenance/systemUpdate/systemUpdate.module';
import { TechnicalSupportDownloadModule } from './pages/support/download/technicalSupportDownload.module';
import { UserInterfaceModule } from './pages/localConfiguration/userInterface/userInterface.module';

/**
 *
 * @type {ModuleWithProviders}
 */
const LoadbalancerRouting: ModuleWithProviders<any> = RouterModule.forChild([
    {
        path: 'lb/:applicationId',
        component: LoadbalancerComponent,
        canActivate: [AuthGuard, EnvironmentGuard, SocketGuard, EnduranceResolverGuard],
        resolve: {
            nodeState: ClusterStatusResolver,
            pulseHaState: PulseHaServicesResolvers
        },
        children: [
            {
                path: 'overview',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        component: OverviewComponent,
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'details/:graphType/:period',
                        canActivate: [RbacPageGuard],
                        component: OverviewDetailsComponent
                    },
                ]
            },
            {
                path: 'local-configuration',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'sys-date-time',
                        canActivate: [RbacPageGuard],
                        component: DateTimeComponent,
                    },
                    {
                        path: 'execute',
                        canActivate: [RbacPageGuard],
                        component: ExecuteComponent
                    },
                    {
                        path: 'hostname-dns',
                        canActivate: [RbacPageGuard],
                        component: HostnameDnsComponent,
                        resolve: {
                            hostnameDns: HostnameResolver
                        }
                    },
                    {
                        path: 'licence',
                        canActivate: [RbacPageGuard],
                        component: LicenceComponent,
                        resolve: {
                            licenceInfo: LicenceResolver
                        }
                    },
                    {
                        path: 'phys-adv-conf',
                        canActivate: [RbacPageGuard],
                        component: PhysicalAdvConfComponent
                    },
                    {
                        path: 'portal-management',
                        canActivate: [RbacPageGuard],
                        component: PortalManagementComponent
                    },
                    {
                        path: 'collectors/prometheus',
                        canActivate: [RbacPageGuard],
                        component: PrometheusComponent
                    },
                    {
                        path: 'routing',
                        canActivate: [RbacPageGuard],
                        component: RoutingComponent,
                        resolve: {
                            routingConfig: RoutingResolver,
                            interfaces: InterfaceResolver
                        }
                    },
                    {
                        path: 'snmp',
                        canActivate: [RbacPageGuard],
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: SnmpComponent,
                            },
                            {
                                path: 'config',
                                canActivate: [RbacPageGuard],
                                component: SnmpConfigComponent,
                            },
                            {
                                path: 'user',
                                canActivate: [RbacPageGuard],
                                component: SnmpUserComponent,
                            },
                            {
                                path: 'user/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: SnmpUserComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'user-interface',
                        canActivate: [RbacPageGuard],
                        component: UserInterfaceComponent
                    }
                ]
            },
            {
                path: 'network',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'interfaces',
                        children: [
                            {
                                path: '',
                                component: InterfaceConfigurationComponent,
                            },
                            {
                                path: 'add-vlan',
                                canActivate: [RbacPageGuard],
                                component: AddEditVlanComponent,
                            },
                            {
                                path: 'edit-vlan/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditVlanComponent,
                            },
                            {
                                path: 'add-bond',
                                canActivate: [RbacPageGuard],
                                component: AddBondComponent,
                            },
                            {
                                path: 'break-bond/:interfaceId',
                                canActivate: [RbacPageGuard],
                                component: BreakBondComponent,
                            },
                            {
                                path: 'edit-interface/:interfaceId',
                                canActivate: [RbacPageGuard],
                                component: EditInterfaceComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'static',
                        children: [
                            {
                                path: 'ips',
                                children: [
                                    {
                                        path: '',
                                        canActivate: [RbacPageGuard],
                                        component: SipsComponent
                                    },
                                    {
                                        path: 'add',
                                        canActivate: [RbacPageGuard],
                                        component: AddEditSipComponent
                                    },
                                    {
                                        path: 'edit/:id',
                                        canActivate: [RbacPageGuard],
                                        component: AddEditSipComponent
                                    },
                                    {
                                        path: 'no-access',
                                        component: NoAccessComponent
                                    },
                                    {
                                        path: 'page-error',
                                        component: PageErrorComponent
                                    }
                                ]
                            },
                            // {
                            //     path: 'routes',
                            //     children: [
                            //         {
                            //             path: '',
                            //             canActivate: [LbPageGuard],
                            //             component: StaticRoutesComponent
                            //         },
                            //         {
                            //             path: 'add',
                            //             canActivate: [LbPageGuard],
                            //             component: AddEditStaticRouteComponent
                            //         },
                            //         {
                            //             path: 'edit/:id',
                            //             canActivate: [LbPageGuard],
                            //             component: AddEditStaticRouteComponent
                            //         },
                            //         {
                            //             path: 'no-access',
                            //             component: NoAccessComponent
                            //         },
                            //         {
                            //             path: 'page-error',
                            //             component: PageErrorComponent
                            //         }
                            //     ]
                            // }
                        ]
                    },
                    {
                        path: 'floating',
                        children: [
                            {
                                path: 'ips',
                                children: [
                                    {
                                        path: '',
                                        canActivate: [RbacPageGuard],
                                        component: FipsIpsComponent
                                    },
                                    {
                                        path: 'add',
                                        canActivate: [RbacPageGuard],
                                        component: AddEditFipComponent
                                    },
                                    {
                                        path: 'edit/:id',
                                        canActivate: [RbacPageGuard],
                                        component: AddEditFipComponent
                                    },
                                    {
                                        path: 'no-access',
                                        component: NoAccessComponent
                                    },
                                    {
                                        path: 'page-error',
                                        component: PageErrorComponent
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'ip-groups',
                        children: [
                            {
                                path: '',
                                component: IpGroupsComponent,
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: AddEditIpGroupsComponent,
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditIpGroupsComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'regions',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: RegionsComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: AddEditRegionComponent
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditRegionComponent
                            },
                            {
                                path: ':id',
                                canActivate: [RbacPageGuard],
                                component: RegionDetailsComponent
                            }
                        ]
                    },
                ]
            },
            {
                path: 'cluster-configuration',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'advanced-services',
                        canActivate: [RbacPageGuard],
                        component: AdvancedServicesComponent
                    },
                    {
                        path: 'cipher-lists',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: CipherListComponent
                            },
                            {
                                path: 'new',
                                canActivate: [RbacPageGuard],
                                component: AddEditCipherListComponent
                            },
                            {
                                path: 'duplicate/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditCipherListComponent,
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditCipherListComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'gslb',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: NoAccessComponent,
                            },
                            //         {
                            //             path: 'global-names/add',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditGlobalNamesComponent,
                            //         },
                            //         {
                            //             path: 'global-names/edit/:id',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditGlobalNamesComponent,
                            //         },
                            //         {
                            //             path: 'members/add',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditMembersComponent,
                            //         },
                            //         {
                            //             path: 'members/edit/:id',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditMembersComponent,
                            //         },
                            //         {
                            //             path: 'pools/add',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditPoolsComponent,
                            //         },
                            //         {
                            //             path: 'pools/edit/:id',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditPoolsComponent,
                            //         },
                            //         {
                            //             path: 'topologies/add',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditTopologyComponent,
                            //         },
                            //         {
                            //             path: 'topologies/edit/:id',
                            //             canActivate: [RbacPageGuard],
                            //             component: AddEditTopologyComponent,
                            //         },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            // {
                            //     path: 'page-error',
                            //     component: PageErrorComponent
                            // }
                        ]
                    },
                    {
                        path: 'ha-config',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: HaConfComponent
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: AddRestoreNodeComponent
                            },
                            {
                                path: 'restore/:id',
                                canActivate: [RbacPageGuard],
                                component: AddRestoreNodeComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'healthchecks',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: HealthChecksComponent,
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: AddEditHealthChecksComponent,
                            },
                            {
                                path: 'duplicate/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditHealthChecksComponent,
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditHealthChecksComponent,
                            },
                            {
                                path: 'view/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditHealthChecksComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'services/backends/stick-table/:backend',
                        canActivate: [RbacPageGuard],
                        component: L7StickTableComponent,
                        resolve: {
                            entries: StickTableResolver
                        },
                    },
                    {
                        path: 'manage-acls',
                        canActivate: [RbacPageGuard],
                        component: ManageACLsComponent,
                        resolve: {
                            backends: BackendsResolver,
                            frontends: FrontendsResolver,
                            acls: AclsResolver
                        },
                    },
                    {
                        path: 'manage-acls/acls-details/:serviceType/:frontendId',
                        canActivate: [RbacPageGuard],
                        component: AclsDetailsComponent,
                        children: []
                    },
                    {
                        path: 'manage-services',
                        canActivate: [RbacPageGuard],
                        component: ManageServicesComponent,
                        children: []
                    },
                    {
                        path: 'manage-services/service-details/:serviceType/:id',
                        canActivate: [RbacPageGuard],
                        component: ServiceDetailsComponent
                    },
                    {
                        path: 'pulse-config',
                        canActivate: [RbacPageGuard],
                        component: PulseConfComponent,
                    },
                    {
                        path: 'ssl-certificate',
                        children: [
                            {
                                path: '',
                                component: SSLCertificateComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            },
                            {
                                path: 'cert/add',
                                canActivate: [RbacPageGuard],
                                component: AddEditCertComponent,
                            },
                            {
                                path: 'cert/upload',
                                canActivate: [RbacPageGuard],
                                component: UploadPemPfxComponent,
                            },
                            {
                                path: 'cert/apply-csr/:id',
                                canActivate: [RbacPageGuard],
                                component: UploadPemPfxComponent,
                            },
                            {
                                path: 'cert/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditCertComponent,
                            },
                            {
                                path: 'csr/new',
                                canActivate: [RbacPageGuard],
                                component: AddEditCSRComponent,
                            },
                            {
                                path: 'csr/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditCSRComponent,
                            },
                            {
                                path: 'self-signed/new',
                                canActivate: [RbacPageGuard],
                                component: AddEditCSRComponent,
                            },
                            {
                                path: 'ca/new',
                                canActivate: [RbacPageGuard],
                                component: AddEditCertComponent,
                            },
                            {
                                path: 'ca/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditCertComponent,
                            },
                            {
                                path: 'crl/new',
                                canActivate: [RbacPageGuard],
                                component: AddEditCertComponent,
                            },
                            {
                                path: 'crl/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditCertComponent,
                            },
                        ]
                    }
                ]
            },
            {
                path: 'maintenance',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'backup-restore',
                        canActivate: [RbacPageGuard],
                        children: [
                            {
                                path: '',
                                component: BackupRestoreComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            },
                            {
                                path: 'restore',
                                canActivate: [RbacPageGuard],
                                component: RestoreBackupComponent
                            },
                            {
                                path: 'upload',
                                canActivate: [RbacPageGuard],
                                component: UploadBackupComponent
                            },
                        ]
                    },
                    {
                        path: 'fallback-pages',
                        children: [
                            {
                                path: '',
                                canActivate: [RbacPageGuard],
                                component: FallbackPagesComponent,
                            },
                            {
                                path: 'add',
                                canActivate: [RbacPageGuard],
                                component: AddEditFallbackPageComponent
                            },
                            {
                                path: 'edit/:id',
                                canActivate: [RbacPageGuard],
                                component: AddEditFallbackPageComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'services',
                        canActivate: [RbacPageGuard],
                        component: ServicesComponent
                    },
                    {
                        path: 'software-update',
                        canActivate: [RbacPageGuard],
                        children: [
                            {
                                path: '',
                                component: SystemUpdateComponent,
                            },
                            {
                                path: 'manual-update',
                                component: ManualUpdateComponent,
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'users',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'list'
                            },
                            {
                                path: 'list',
                                canActivate: [RbacPageGuard],
                                component: EnduranceUserListComponent
                            },
                            {
                                path: 'user/add',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditUserComponent
                            },
                            {
                                path: 'user/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditUserComponent
                            },
                            {
                                path: 'role/add',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditRolesComponent
                            },
                            {
                                path: 'role/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditRolesComponent
                            },
                            {
                                path: 'role/edit/:id/:duplicate',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditRolesComponent
                            },
                            {
                                path: 'group/add',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditGroupsComponent
                            },
                            {
                                path: 'group/edit/:id',
                                canActivate: [RbacPageGuard],
                                component: EnduranceAddEditGroupsComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    }
                ]
            },
            {
                path: 'reports',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'gslb-reports',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'no-access'
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            },
                            // {
                            //     path: ':id',
                            //     canActivate: [RbacPageGuard],
                            //     component: GslbReportsComponent
                            // }
                        ]
                    }
                ]
            },
            {
                path: 'view-configuration',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: ':service',
                        canActivate: [RbacPageGuard],
                        component: ConfigurationComponent
                    }
                ]
            },
            {
                path: 'view-logs',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'load-balancer',
                        canActivate: [RbacPageGuard],
                        component: LogsComponent,
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    }
                ]
            },
            {
                path: 'notifications',
                canActivate: [RbacPageGuard],
                component: NotificationsComponent
            },
            {
                path: 'support',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'no-access'
                    },
                    {
                        path: 'no-access',
                        component: NoAccessComponent
                    },
                    {
                        path: 'page-error',
                        component: PageErrorComponent
                    },
                    {
                        path: 'download',
                        canActivate: [RbacPageGuard],
                        children: [
                            {
                                path: '',
                                component: TechnicalSupportDownloadComponent
                            },
                            {
                                path: 'no-access',
                                component: NoAccessComponent
                            },
                            {
                                path: 'page-error',
                                component: PageErrorComponent
                            }
                        ]
                    },
                    {
                        path: 'contact-us',
                        canActivate: [RbacPageGuard],
                        children: [
                            {
                                path: '',
                                component: ContactUsComponent
                            },

                        ]
                    }
                ]
            },
            {
                path: 'service/add',
                canActivate: [RbacPageGuard],
                component: NewServiceComponent
            },
            {
                path: 'service/edit/:serviceId',
                canActivate: [RbacPageGuard],
                component: NewServiceComponent
            },
            {
                path: 'acl/:serviceType/add',
                canActivate: [RbacPageGuard],
                component: NewServiceComponent
            },
            {
                path: 'acl/:serviceType/edit/:id',
                canActivate: [RbacPageGuard],
                component: NewServiceComponent
            },
            {
                path: 'no-access',
                component: NoAccessComponent
            },
            {
                path: 'page-error',
                component: PageErrorComponent
            },
            {
                path: '**',
                redirectTo: '/404',
            }
        ]
    }
]);

/**
 *
 */
@NgModule({
    imports: [
        LoadbalancerRouting,
        SharedModule,
        LoadbalancerSharedModule,
        // Cluster Config
        AclsDetailsModule,
        AddEditHealthChecksModule,
        AdvancedServicesModule,
        CipherListModule,
        GslbModule,
        HaConfModule,
        HealthChecksModule,
        L7StickTableModule,
        ManageACLsModule,
        ManageServicesModule,
        NewServiceModule,
        PulseConfModule,
        SSLCertificateModule,
        ServiceDetailsModule,
        // Network
        FipsIpsModule,
        IpGroupsModule,
        NetworkModule,
        SipsModule,
        StaticRoutesModule,
        // Local Config
        DateTimeModule,
        ExecuteModule,
        HostnameDnsModule,
        LicenceModule,
        PhysicalAdvConfigModule,
        PortalManagementModule,
        PrometheusModule,
        RoutingModule,
        SnmpModule,
        UserInterfaceModule,
        // Maintenance
        BackupRestoreModule,
        EnduranceAddEditRolesModule,
        EnduranceUserListModule,
        FallbackPagesModule,
        // GroupsModule,
        NotificationsModule,
        ServicesModule,
        SystemUpdateModule,
        // Reports
        GslbReportsModule,
        // Support
        ContactUsModule,
        TechnicalSupportDownloadModule,
        // Overview
        OverviewModule,
        // View Config
        ConfigurationsModule,
        // View logs
        LogsModule,
        // Extras
        NgOptimizedImage
    ],
    declarations: [
        LoadbalancerComponent
    ],
    exports: [
        LoadbalancerSharedModule,
    ],
    providers: [
        AuthGuard,
        ClusterStatusResolver,
        EnduranceRBACResourcesApiService,
        EnduranceRbacApiService,
        EnduranceRbacMenuResolvers,
        PulseHaServicesResolvers,
        ServicesUtilsService,
        SocketGuard,
        SupportApiService
    ]
})

export class LoadbalancerModule {
}
